<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const initialState = {
  name: '',
  description: '',
  productName: '',
  features: {
    conversationFaqs: false,
    memories: false,
    citations: false,
    contactAttributes: false,
  },
  llm: {
    provider: '',
    model: '',
    baseUrl: '',
    apiKey: '',
    strictJson: null,
  },
};

const PROVIDER_OPTIONS = [
  { value: '', labelKey: 'CAPTAIN.ASSISTANTS.FORM.LLM.PROVIDER.PLACEHOLDER' },
  {
    value: 'openai',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.LLM.PROVIDER.OPTIONS.OPENAI',
  },
  {
    value: 'anthropic',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.LLM.PROVIDER.OPTIONS.ANTHROPIC',
  },
  {
    value: 'openrouter',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.LLM.PROVIDER.OPTIONS.OPENROUTER',
  },
  {
    value: 'qwen',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.LLM.PROVIDER.OPTIONS.QWEN',
  },
  {
    value: 'custom_openai_compatible',
    labelKey:
      'CAPTAIN.ASSISTANTS.FORM.LLM.PROVIDER.OPTIONS.CUSTOM_OPENAI_COMPATIBLE',
  },
];

// Per-provider Base URL behavior. Native RubyLLM providers (anthropic /
// openrouter) bake their own endpoint in — admins should never set a
// base URL for them. OpenAI accepts an override for Azure / proxy
// setups but is optional. Qwen routes through the openai provider with
// DashScope's OpenAI-compatible endpoint (international by default;
// admins in mainland China switch to dashscope.aliyuncs.com).
// custom_openai_compatible has no default — admin must supply.
const BASE_URL_BY_PROVIDER = {
  openai: { mode: 'optional', preset: '' },
  anthropic: { mode: 'hidden', preset: '' },
  openrouter: { mode: 'hidden', preset: '' },
  qwen: {
    mode: 'editable',
    preset: 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1',
  },
  custom_openai_compatible: { mode: 'required', preset: '' },
};

const state = reactive({ ...initialState });

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  description: getErrorMessage('description'),
  productName: getErrorMessage('productName'),
}));

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.name = assistant.name;
  state.description = assistant.description;
  state.productName = config.product_name;
  state.features = {
    conversationFaqs: config.feature_faq || false,
    memories: config.feature_memory || false,
    citations: config.feature_citation || false,
    contactAttributes: config.feature_contact_attributes || false,
  };
  state.llm = {
    provider: config.llm_provider || '',
    model: config.llm_model || '',
    baseUrl: config.llm_base_url || '',
    apiKey: '',
    // null = follow provider-aware default; true/false = explicit override
    strictJson:
      config.llm_strict_json === undefined ? null : config.llm_strict_json,
  };
};

// Frontend never sees the saved key (the jbuilder strips it). Surface a
// boolean so we can change the placeholder text and signal that an
// existing key is on file.
const hasSavedApiKey = computed(() =>
  Boolean(props.assistant?.has_llm_api_key)
);

// Tracks whether the admin clicked "Clear saved key". Submitting with
// this set sends an explicit empty string for llm_api_key, which the
// controller treats as "wipe the encrypted column".
const clearSavedApiKeyOnSave = ref(false);

// When the provider dropdown is reset to "" (use installation default),
// the admin's intent is "fall back to global Captain config entirely".
// Clear sibling fields so the leftover state doesn't keep us on the
// per-assistant override path. The saved API key must be cleared
// server-side (it's not exposed to the client), so flip the flag too.
//
// For named providers, also pre-populate the Base URL with the right
// preset (or clear it for providers where RubyLLM bakes the endpoint in)
// so admins never have to know a URL by heart.
const handleProviderChange = () => {
  if (state.llm.provider === '') {
    state.llm.model = '';
    state.llm.baseUrl = '';
    state.llm.apiKey = '';
    state.llm.strictJson = null;
    if (hasSavedApiKey.value) clearSavedApiKeyOnSave.value = true;
    return;
  }

  const config = BASE_URL_BY_PROVIDER[state.llm.provider];
  if (!config) return;

  // Always overwrite when switching providers — a leftover URL from the
  // previous provider would be wrong (and silently misroute the call).
  state.llm.baseUrl = config.preset;
};

// Whether the Base URL input should render for the current provider.
const baseUrlMode = computed(() => {
  const config = BASE_URL_BY_PROVIDER[state.llm.provider];
  // Unknown / blank provider → fall back to "editable" so legacy data
  // stays visible. Default-installation case is already handled above.
  return config ? config.mode : 'editable';
});

const showBaseUrl = computed(() => baseUrlMode.value !== 'hidden');

const handleBasicInfoUpdate = async () => {
  const result = await Promise.all([
    v$.value.name.$validate(),
    v$.value.description.$validate(),
    v$.value.productName.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    name: state.name,
    description: state.description,
    config: {
      ...props.assistant.config,
      product_name: state.productName,
      feature_faq: state.features.conversationFaqs,
      feature_memory: state.features.memories,
      feature_citation: state.features.citations,
      feature_contact_attributes: state.features.contactAttributes,
      llm_provider: state.llm.provider || null,
      llm_model: state.llm.model || null,
      llm_base_url: state.llm.baseUrl || null,
      llm_strict_json: state.llm.strictJson,
    },
  };

  // API key handling has three modes:
  //   1. Admin typed a new key → send it (replaces saved key)
  //   2. Admin clicked "Clear saved key" → send empty string (wipes saved key)
  //   3. Admin left field blank → omit the param (preserves saved key)
  if (state.llm.apiKey.trim() !== '') {
    payload.llm_api_key = state.llm.apiKey;
  } else if (clearSavedApiKeyOnSave.value) {
    payload.llm_api_key = '';
  }

  emit('submit', payload);
  // After save, our local flag is stale relative to the server's truth;
  // reset so a subsequent edit doesn't accidentally re-clear.
  clearSavedApiKeyOnSave.value = false;
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Input
      v-model="state.name"
      :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <Input
      v-model="state.productName"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
      :message="formErrors.productName"
      :message-type="formErrors.productName ? 'error' : 'info'"
    />

    <Editor
      v-model="state.description"
      :label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
      :message="formErrors.description"
      :message-type="formErrors.description ? 'error' : 'info'"
      :max-length="4000"
      class="z-0"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.TITLE') }}
      </label>
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2">
          <input v-model="state.features.conversationFaqs" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.memories" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.citations" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.contactAttributes" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONTACT_ATTRIBUTES') }}
        </label>
      </div>
    </div>

    <div class="flex flex-col gap-3 pt-2 border-t border-n-weak">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.LLM.TITLE') }}
        </label>
        <span class="text-xs text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.FORM.LLM.DESCRIPTION') }}
        </span>
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.LLM.PROVIDER.LABEL') }}
        </label>
        <select
          v-model="state.llm.provider"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
          @change="handleProviderChange"
        >
          <option
            v-for="option in PROVIDER_OPTIONS"
            :key="option.value || 'default'"
            :value="option.value"
          >
            {{ t(option.labelKey) }}
          </option>
        </select>
      </div>
      <Input
        v-model="state.llm.model"
        :label="t('CAPTAIN.ASSISTANTS.FORM.LLM.MODEL.LABEL')"
        :placeholder="t('CAPTAIN.ASSISTANTS.FORM.LLM.MODEL.PLACEHOLDER')"
        :message="t('CAPTAIN.ASSISTANTS.FORM.LLM.MODEL.HELP')"
        message-type="info"
      />
      <Input
        v-if="showBaseUrl"
        v-model="state.llm.baseUrl"
        :label="t('CAPTAIN.ASSISTANTS.FORM.LLM.BASE_URL.LABEL')"
        :placeholder="t('CAPTAIN.ASSISTANTS.FORM.LLM.BASE_URL.PLACEHOLDER')"
        :message="
          baseUrlMode === 'required'
            ? t('CAPTAIN.ASSISTANTS.FORM.LLM.BASE_URL.HELP_REQUIRED')
            : baseUrlMode === 'editable'
              ? t('CAPTAIN.ASSISTANTS.FORM.LLM.BASE_URL.HELP_EDITABLE')
              : t('CAPTAIN.ASSISTANTS.FORM.LLM.BASE_URL.HELP_OPTIONAL')
        "
        message-type="info"
      />
      <Input
        v-model="state.llm.apiKey"
        type="password"
        :label="t('CAPTAIN.ASSISTANTS.FORM.LLM.API_KEY.LABEL')"
        :placeholder="
          hasSavedApiKey && !clearSavedApiKeyOnSave
            ? t('CAPTAIN.ASSISTANTS.FORM.LLM.API_KEY.PLACEHOLDER_SAVED')
            : t('CAPTAIN.ASSISTANTS.FORM.LLM.API_KEY.PLACEHOLDER')
        "
        :message="t('CAPTAIN.ASSISTANTS.FORM.LLM.API_KEY.HELP')"
        message-type="info"
      />
      <div v-if="hasSavedApiKey && !clearSavedApiKeyOnSave" class="-mt-1">
        <button
          type="button"
          class="text-xs text-n-ruby-11 hover:underline"
          @click="clearSavedApiKeyOnSave = true"
        >
          {{ t('CAPTAIN.ASSISTANTS.FORM.LLM.API_KEY.CLEAR_BUTTON') }}
        </button>
      </div>
      <div v-if="clearSavedApiKeyOnSave" class="-mt-1 text-xs text-n-ruby-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.LLM.API_KEY.CLEAR_PENDING') }}
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            type="checkbox"
            :checked="state.llm.strictJson !== false"
            @change="
              event =>
                (state.llm.strictJson = event.target.checked ? null : false)
            "
          />
          {{ t('CAPTAIN.ASSISTANTS.FORM.LLM.STRICT_JSON.LABEL') }}
        </label>
        <span class="text-xs text-n-slate-11 pl-6">
          {{ t('CAPTAIN.ASSISTANTS.FORM.LLM.STRICT_JSON.HELP') }}
        </span>
      </div>
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleBasicInfoUpdate"
      />
    </div>
  </div>
</template>
